import { Settings, X } from 'lucide-react';
import edit from 'images/icon/edit.png';
import * as _ from 'lodash';
import * as React from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Collapse } from 'reactstrap';
import styled from 'styled-components';
import { Checkbox } from '../../../components_v2/filterList/style/Style';
import InputSearch from '../../../components_v2/input/InputSearch';
import { BlackImg } from '../../../components_v2/Selector/CategorySelector';
import { DandDDots } from '../../../containers_v2/settings/subSettings/statusesAndTagsSettings';
import { Dot } from '../../../styles/global/css/Dot';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import { Open } from '../../../styles/global/css/Open';
import { BorderColor, DarkGreySidely2, GreySidely2, LightGreySidely2, SidelyBlack, SurfaceColor } from '../../../styles/global/css/Utils';
import { Translate, translateToString } from '../../../styles/global/translate';
import usePermission from '../../permissions/usePermission';
import { FlexDiv } from '../../products/style';

export type Column = {
	label: string;
	value: string;
	checked: boolean;
	sequence: number;
	categoryIndex: number | undefined;
}

const Wrapper = styled.div`
	cursor: default;
	font-size: 12px;
`;

export const PopupWrapper = styled.div<{ noSave?: boolean }>`
	display: grid;
	grid-template-rows: 30px 1fr ${p => p.noSave ? '' : '50px'};
	grid-gap: 10px;
	height: 100%;
`;

export const GridContainer = styled.div<{ scroll?: boolean, min?: string, gap?: string, padding?: string }>`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(${p => p.min ?? '300px'}, 1fr));
	height: 100%;
	min-height: 0;
	padding-right: 10px;
	padding: ${p => p.padding ?? '0 10px 0 0'};
	font-weight: 400;
	${p => p.scroll ? 'overflow: auto;' : ''}
	${p => p.gap ? 'gap: 20px;' : ''}
`;

export const GridElement = styled.div<{ index: number }>`
	display: flex;
	flex-direction: column;
	position: relative;
	gap: 10px;
	user-select: none;
	height: 100%;
	min-height: 0;
	padding-left: 10px;
	padding-right: ${p => p.index === 0 ? '8px' : '0'};
	border-bottom: 2px solid ${BorderColor};
	&::before {
		content: '';
		position: absolute;
		bottom: 0;
		right: 0;
		width: 2px;
		height: 93%;
		background-color: ${p => p.index === 0 ? BorderColor : 'transparent'};
	}
	@media (max-width: 928px) {
		&::before {
			display: none;
		}
		padding: 10px;
		padding-top: ${p => p.index === 1 ? '20px' : '10px'};
	}
`;

export const Title = styled.h1`
	font-size: 15px;
	font-weight: 600;
	padding-left: 10px;
`;

const LocalCollapse = styled(Collapse)`
	width: inherit;
	display: flex;
	&:not(.show) &:collapse {
		display: none;
	}
`;

const CollapseTitle = styled.div`
	font-size: 14px;
	font-weight: 500;
	padding: 10px 0;
`;

const SelectedColumn = styled.div`
	display: flex;
	align-items: center;
	height: 40px;
	min-height: 40px;
	width: 100%;
	padding-left: 10px;
	border-radius: 5px;
	border: 1px solid ${BorderColor};
	background-color: ${SurfaceColor};
	color: ${DarkGreySidely2};
`;

const ResponsiveIcon = styled.div<{ titleHover?: string }>`
	display: flex;
	align-items: center;
	width: 150px;
	gap: 4px;
	cursor: pointer;
	position: relative;
    white-space:nowrap;
	@media only screen and (max-width: 1050px) {
		width: 100%;
		${p => p.titleHover ? `&:hover {
                ::before {
                    content: '${p.titleHover}';
                    position: absolute;
                    transform: translate(-45%, 110%);
                    background-color: ${SurfaceColor};
                    font-weight: 400;
                    font-size: 12px;
                    padding: 5px;
                    border: 1px solid ${BorderColor};
                    border-radius: 5px;
                    z-index: 100;
                }
            }` : ''}
	}
`;

const LongIcon = styled.div`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-size: 13px;
	font-weight: 500;
	@media only screen and (max-width: 1050px) {
		display: none;
	};
`;

export function TitleAndChild(props: { title: string, children: React.ReactNode, isDraggable?: boolean, onEdit?: (e: React.MouseEvent<HTMLImageElement>) => void, colorMarker?: string, icon?: React.ReactNode }) {
	const [isOpen, setIsOpen] = React.useState(true);
	return <>
		<FlexDiv justify='space-between' padding='0 10px 0 0' height='30px' cursor='pointer' onClick={() => setIsOpen(!isOpen)} backgroundColor={SurfaceColor}>
			{props.isDraggable || props.colorMarker || props.icon ? <FlexDiv gap='8px' align='center' height='100%'>
				{props.isDraggable ? <DandDDots /> : props.icon ? props.icon : <Dot color={props.colorMarker ?? 'transparent'} size='6px' />}
				<CollapseTitle>{props.title}</CollapseTitle>
			</FlexDiv> :
				<CollapseTitle>{props.title}</CollapseTitle>
			}
			{props.onEdit ? <FlexDiv gap='8px' align='center' height='100%'>
				<BlackImg size='18px' src={edit} onClick={props.onEdit} />
				<Open isOpen={isOpen} size={11} />
			</FlexDiv> :
				<Open isOpen={isOpen} size={11} />
			}
		</FlexDiv>
		<div><LocalCollapse isOpen={isOpen}>{props.children}</LocalCollapse></div>
	</>;
}

export const DialogPopup = styled.dialog`
	width: 70vw;
	height: 75vh;
	padding: 25px 15px 10px 25px;
	border-radius: 10px;
	position: fixed;
	border: none;
	display: none;
	opacity: 0;
	transition-property: overlay display opacity;
	transition-duration: 0.2s;
	transition-behavior: allow-discrete;
	&::backdrop {
		transition-property: overlay display opacity;
		transition-duration: 0.2s;
		transition-behavior: allow-discrete;
		opacity: 0;
		background-color: color-mix(in oklab, ${SidelyBlack} 50%, transparent);
	}
	&[open] {
		display: block;
		opacity: 1;
	}
	&[open]::backdrop {
		opacity: 1;
	}
	@starting-style {
		&[open], &[open]::backdrop {
			opacity: 0;
		}
	}
`;

export function ColumnOrganizer(props: {
	columnsByCategory: { category: string, columns: Column[] }[],
	setColumnsByCategory: React.Dispatch<React.SetStateAction<{ category: string, columns: Column[] }[]>>,
	translationPath: string,
	categoryTranslationPath?: string,
	wrapperRef?: React.RefObject<HTMLDivElement>,
	setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>,
	onSort?: (values: string[]) => void,
	setHiddenColumns?: (values: string[]) => void,
	maxSelected?: number,
}) {
	const { columnsByCategory, setColumnsByCategory, translationPath, categoryTranslationPath, wrapperRef, setIsOpen, onSort, setHiddenColumns, maxSelected } = props;
	const [inputValue, setInputValue] = React.useState('');
	const [selectAll, setSelectAll] = React.useState(columnsByCategory.flatMap(v => v.columns).every(v => v.checked));
	const sortedSelectedColumns = React.useMemo(() =>
		columnsByCategory.flatMap(v => v.columns).filter(v => v.checked).sort((a, b) => a.sequence - b.sequence)
	, [columnsByCategory]);

	React.useEffect(() => {
		setSelectAll(columnsByCategory.flatMap(v => v.columns).every(v => v.checked));
	}, [columnsByCategory]);

	function onDragEnd({ source, destination }) {
		if (!destination || !source || source.index === destination.index) return;
		const result = columnsByCategory.flatMap(v => v.columns).sort((a, b) => a.sequence - b.sequence);
		const [removed] = result.splice(source.index, 1);
		result.splice(destination.index, 0, removed);
		result.forEach((r, i) => r.sequence = i);
		setColumnsByCategory([...columnsByCategory]);
	}

	return (
		<PopupWrapper innerRef={wrapperRef ? React.useCallback(node => wrapperRef.current = node, []) : undefined} noSave={!onSort || !setHiddenColumns}>
			<Title><Translate id={`${translationPath}.title`} options={{ variables: [['MAX_LINKED_CHARTS', maxSelected ? `(max ${maxSelected.toString()})` : '']] }} /></Title>
			<GridContainer>
				<GridElement index={0}>
					<InputSearch name='search-views' type='text' value={inputValue} onChange={(value) => setInputValue(value as string)} placeholder={translateToString(`${translationPath}.search_columns`)} inputStyle={
						{
							backgroundColor: LightGreySidely2,
							border: 'none',
							borderRadius: '7px',
							width: '240px',
							padding: '5px 10px',
							placeholderColor: GreySidely2,
						}
					} />
					<FlexDiv flow='column' align='stretch' height='100%' overflow='auto' scrollGutter={true} padding='10px 0'>
						<FlexDiv gap='8px' cursor='pointer' color={DarkGreySidely2} padding='0 0 10px 0' onClick={() => {
							const val = !selectAll;
							setSelectAll(val);
							columnsByCategory.flatMap(v => v.columns).forEach(v => v.checked = val);
							setColumnsByCategory([...columnsByCategory]);
						}}>
							<Checkbox isActive={selectAll} />
							<Translate id={`${translationPath}.select_all`} />
						</FlexDiv>
						{columnsByCategory.map((category, i) => {
							if (category.columns.length === 0) return null;
							const filteredColumns = category.columns.filter(v => v.label.toLowerCase().includes(inputValue.toLowerCase()));
							if (filteredColumns.length === 0) return null;
							return <TitleAndChild key={category.category} title={translateToString(categoryTranslationPath ? `${categoryTranslationPath}.${category.category}` : category.category)}>
								<FlexDiv flow='column' align='stretch' gap='8px' padding='6px 0 10px 0'>
									{filteredColumns.map((column) => {
										return <FlexDiv gap='8px' cursor='pointer' color={DarkGreySidely2} key={column.value} onClick={(e) => {
											e.stopPropagation();
											const index = columnsByCategory[i].columns.findIndex(v => v.value === column.value);
											columnsByCategory[i].columns[index].checked = !column.checked;
											setColumnsByCategory([...columnsByCategory]);
										}} ><Checkbox isActive={column.checked} /><div>{column.label}</div></FlexDiv>;
									})}
								</FlexDiv>
							</TitleAndChild>;
						})}
					</FlexDiv>
				</GridElement>
				<GridElement index={1}>
					<div style={{ fontSize: '14px', fontWeight: '500', paddingLeft: '10px' }}><Translate id={`${translationPath}.selected_columns`} /> ({sortedSelectedColumns.length})</div>
					<DragDropContext onDragEnd={onDragEnd}>
						<Droppable droppableId='companies'>
							{(provided): React.ReactElement<HTMLElement> => (
								<FlexDiv {...provided.droppableProps} innerRef={provided.innerRef} className="companies" flow='column' align='stretch' gap='10px' overflow='auto' scrollGutter padding='10px 4px 10px 0px'>
									{sortedSelectedColumns.map((column) =>
										<Draggable key={column.value} draggableId={column.value} index={column.sequence}>
											{(provided): React.ReactElement<HTMLElement> => (
												<FlexDiv width='100%' gap='6px' innerRef={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
													<DandDDots />
													<SelectedColumn>
														{column.label}
													</SelectedColumn>
													<X style={{ cursor: 'pointer' }}
														onClick={() => {
															const toClose = columnsByCategory.flatMap(v => v.columns).find(v => v.value === column.value);
															if (toClose) {
																toClose.checked = false;
																setColumnsByCategory([...columnsByCategory]);
															}
														}} />
												</FlexDiv>
											)}
										</Draggable>
									)}
									{provided.placeholder}
								</FlexDiv>
							)}
						</Droppable>
					</DragDropContext>
				</GridElement>
			</GridContainer>
			{onSort && setHiddenColumns &&
				<FlexDiv justify='flex-end'>
					<FlexDiv>
						<DefaultButton buttonStyle={2} height='38px' width='130px' onClick={setIsOpen ? () => setIsOpen(false) : undefined}><Translate id='cancel' /></DefaultButton>
						<DefaultButton height='38px' width='130px' onClick={() => {
							const columns = columnsByCategory.flatMap(v => v.columns);
							onSort?.(columns.sort((a, b) => a.sequence - b.sequence).map((col) => col.value));
							setHiddenColumns?.(columns.filter(v => !v.checked).map(v => v.value));
							if (setIsOpen)
								setIsOpen(false);
						}}><Translate id='apply' /></DefaultButton>
					</FlexDiv>
				</FlexDiv>
			}
		</PopupWrapper>
	);
}

export function ColumnOrganizerWrapper(props: {
	columns: Column[],
	categories: readonly string[],
	onSort?: (values: string[]) => void,
	setHiddenColumns?: (values: string[]) => void,
	longIcon?: boolean,
	popup?: boolean,
	translationPath?: string,
}) {
	const [isOpen, setIsOpen] = React.useState(false);
	const [columnsByCategory, setColumnsByCategory] = React.useState<{ category: string, columns: Column[] }[]>([]);
	const ref = React.useRef<HTMLDialogElement | null>(null);
	const wrapperRef = React.useRef<HTMLDivElement | null>(null);
	const hasShelfAudit = usePermission({ objectAction: 'ReadShelfAudit' });
	const translationPath = props.translationPath ? `${props.translationPath}.` : 'company.column_organizer';

	React.useEffect(() => {
		const result = props.categories?.map(category => ({ category, columns: [] as Column[] })) ?? [];
		result.push({ category: 'other', columns: [] });
		props.columns?.forEach(c => {
			if (c.categoryIndex === undefined) result[result.length - 1].columns.push(_.cloneDeep(c));
			else result[c.categoryIndex].columns.push(_.cloneDeep(c));
		});
		if (!hasShelfAudit) {
			const shelfAuditIndex = result.findIndex(v => v.category === 'shelf_audit');
			if (shelfAuditIndex !== -1) result.splice(shelfAuditIndex, 1);
		}

		setColumnsByCategory(result);
	}, [props.columns, isOpen]);

	React.useEffect(() => {
		if (isOpen) {
			const handleEsc = (e: KeyboardEvent) => {
				if (e.key === 'Escape') setIsOpen(false);
			};
			const handleClickOutside = (e: MouseEvent) => {
				if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
					setIsOpen(false);
				}
			};
			document.addEventListener('mousedown', handleClickOutside);
			document.addEventListener('keydown', handleEsc);
			ref.current?.showModal();
			return () => {
				document.removeEventListener('mousedown', handleClickOutside);
				document.removeEventListener('keydown', handleEsc);
			};
		}
		else
			ref.current?.close();
	}, [isOpen]);

	return (
		<Wrapper>
			{
				props.longIcon ?
					<ResponsiveIcon titleHover={translateToString(`${translationPath}.displayed_columns`)} onClick={() => setIsOpen(!isOpen)}>
						<Settings />
						<LongIcon><Translate id={`${translationPath}.displayed_columns`} /></LongIcon>
					</ResponsiveIcon>
					:
					<div style={{ cursor: 'pointer' }} onClick={() => setIsOpen(!isOpen)}><Settings /></div>
			}
			<DialogPopup innerRef={React.useCallback(node => ref.current = node, [])}>
				<ColumnOrganizer
					columnsByCategory={columnsByCategory}
					setColumnsByCategory={setColumnsByCategory}
					translationPath={translationPath}
					wrapperRef={wrapperRef}
					setIsOpen={setIsOpen}
					onSort={props.onSort}
					setHiddenColumns={props.setHiddenColumns}
				/>
			</DialogPopup>
		</Wrapper>
	);
}
